import { SEARCH_TYPE_CARE } from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { grammaticalList } from "core/model/utils/strings";
import {
  Auction,
  GetOntologyType,
  InfectionAndGerms,
  PatientNeeds,
  TransitionalCareFormData,
  ValueOf,
} from "core/types";
import Translations from "translations/types";
import { CHARACTER_LIMIT_DESCRIPTION_TEXT } from "../FormSections/SectionB/TransitionalCareDescription";

export type TransitionalCareFormGetters = Partial<
  Record<
    keyof TransitionalCareFormData,
    (auction: Auction) => ValueOf<TransitionalCareFormData> | null
  >
>;

function getDescriptionTranslationFromObject<
  T extends InfectionAndGerms | PatientNeeds,
>({
  object,
  translationMap,
  translations,
}: {
  object: T | null | undefined;
  translationMap: Record<keyof T, (translations: Translations) => string>;
  translations: Translations;
}): string {
  if (!object) return "";

  return Object.entries(object)
    .map(([key, value]) => {
      if (!value) return null;

      if (key === "other") {
        return `${translationMap[key as keyof T](translations)}: ${value}`;
      }

      return translationMap[key as keyof T](translations);
    })
    .truthy()
    .join(", ");
}

const CARENEEDS_TRANSLATIONS: Record<
  keyof PatientNeeds,
  (translations: Translations) => string
> = {
  __typename: (_: Translations) => "",
  access_drainages: (translations: Translations) =>
    translations.patient.careNeeds.accessDrainages.accessAndDrainages,
  base_care: (translations: Translations) =>
    translations.patient.careNeeds.baseCare,
  basic_medical_care: (translations: Translations) =>
    translations.patient.careNeeds.medicalCare,
  breathing: (translations: Translations) =>
    translations.patient.careNeeds.breathing,
  care_for_children_and_teenagers: (translations: Translations) =>
    translations.patient.careNeeds.careForChildrenAndTeenagers.title,
  care_for_youth: (translations: Translations) =>
    translations.patient.careNeeds.careForYouth.title,
  companionship: (translations: Translations) =>
    translations.patient.careNeeds.companionship,
  compression: (translations: Translations) =>
    translations.patient.careNeeds.compression,
  daily_activities: (translations: Translations) =>
    translations.patient.requiredServices.dailyActivities,
  dialysis: (translations: Translations) =>
    translations.patient.careNeeds.dialysis,
  food: (_: Translations) => "",
  help_at_home: (translations: Translations) =>
    translations.patient.careNeeds.helpAtHome,
  important_information: (_: Translations) => "",
  incontinence: (translations: Translations) =>
    translations.patient.careNeeds.incontinence,
  injection: (translations: Translations) =>
    translations.patient.careNeeds.injection,
  integration_assistance: (translations: Translations) =>
    translations.patient.careNeeds.integrationAssistance.label,
  medication: (translations: Translations) =>
    translations.patient.careNeeds.medication,
  monitoring: (translations: Translations) =>
    translations.patient.careNeeds.monitoring,
  need_for_assistance_description: (translations: Translations) =>
    translations.patient.careNeeds.stepperTitleCare,
  nutrition: (translations: Translations) =>
    translations.patient.careNeeds.nutrition,
  oxygen_therapy: (translations: Translations) =>
    translations.patient.careNeeds.oxygenTherapy.label,
  palliative: (translations: Translations) =>
    translations.patient.palliativeCare.label,
  psych_closed_area: (translations: Translations) =>
    translations.patient.requiredServices.closedArea,
  psych_protected_area: (translations: Translations) =>
    translations.patient.requiredServices.protectedArea,
  psychiatric_care: (translations: Translations) =>
    translations.patient.careNeeds.psychiatric_care.label,
  psychiatric_home_nursing: (translations: Translations) =>
    translations.patient.requiredServices.psychiatricHomeNursing,
  remarks: (_: Translations) => "",
  sociotherapy: (translations: Translations) =>
    translations.patient.requiredServices.sociotherapy,
  static_care_disabled_patients: (_: Translations) => "",
  substitute_offers: (translations: Translations) =>
    translations.patient.requiredServices.substituteOffers,
  support: (translations: Translations) =>
    translations.patient.careNeeds.needSupport,
  wound_care: (translations: Translations) =>
    translations.patient.careNeeds.woundCare,
} as const;

const INFECTION_TRANSLATIONS: Record<
  keyof InfectionAndGerms,
  (translations: Translations) => string
> = {
  __typename: (_: Translations) => "",
  clostridien: (translations: Translations) =>
    translations.ontologies.infectionAndGerms.values.clostridien,
  description: (_: Translations) => "",
  four_mrgn: (translations: Translations) =>
    translations.ontologies.infectionAndGerms.values.four_mrgn,
  hepatitis_c: (translations: Translations) =>
    translations.patient.medicalDiagnosis.infectionsHepatitis,
  mrsa: (translations: Translations) =>
    translations.ontologies.infectionAndGerms.values.mrsa,
  other: (translations: Translations) => translations.general.other,
  remarks: (_: Translations) => "",
  requires_isolation: (translations: Translations) =>
    translations.ontologies.infectionAndGerms.values.requires_isolation,
  three_mrgn: (translations: Translations) =>
    translations.ontologies.infectionAndGerms.values.three_mrgn,
  vre: (translations: Translations) =>
    translations.ontologies.infectionAndGerms.values.vre,
} as const;

export const getTransitionalCareDescriptionText = ({
  auction,
  getOntology,
  translations,
}: {
  auction: Auction;
  getOntology: GetOntologyType;
  translations: Translations;
}) => {
  const fieldObject: Record<string, { title: string; value: string | null }> = {
    carelevel: {
      title: translations.patient.carelevel,
      value: getOntology({
        key: auction.patient?.profile?.financing?.carelevel?.level,
        type: ONTOLOGIES.CARE_LEVEL,
      }),
    },
    careNeeds: {
      title:
        auction.search_type === SEARCH_TYPE_CARE
          ? translations.patient.careNeeds.stepperTitleCare
          : translations.patient.careNeeds.stepperTitle,
      value: getDescriptionTranslationFromObject({
        object: auction.patient.care_needs,
        translationMap: CARENEEDS_TRANSLATIONS,
        translations,
      }),
    },
    infectionAndGerms: {
      title: translations.ontologies.infectionAndGerms.key,
      value: getDescriptionTranslationFromObject({
        object: auction.patient.diagnosis?.infection_and_germs,
        translationMap: INFECTION_TRANSLATIONS,
        translations,
      }),
    },
    location: {
      title: translations.patient.treatmentLocation.title,
      value: `${auction.profile?.search_location?.zipcode}, ${auction.profile?.search_location?.city}`,
    },
    searchRadius: {
      title: translations.patient.searchRadius,
      value: auction.radius_in_meter
        ? `${auction.radius_in_meter / 1000} ${
            translations.abbreviations.kilometers.abbreviation
          }`
        : "",
    },
    solution: {
      title: translations.ontologies.solution.key,
      value: grammaticalList({
        array: auction.solutions,
        getOntology,
        ontologyType: ONTOLOGIES.SOLUTION,
        translations,
      }),
    },
    specializations: {
      title: translations.ontologies.specializations.key,
      value: grammaticalList({
        array: auction.specializations,
        getOntology,
        ontologyType: ONTOLOGIES.SPECIALIZATIONS,
        translations,
      }),
    },
  };

  return Object.values(fieldObject)
    .filter(({ value }) => value != "")
    .map(({ title, value }) => `${title}: ${value}`)
    .join("; ")
    .substring(0, CHARACTER_LIMIT_DESCRIPTION_TEXT);
};
